/**
 * lazyWithReload — React.lazy with a stale-chunk auto-reload guard (CEL-1086).
 *
 * After a deploy, an already-open tab can request a lazy chunk whose hashed
 * filename no longer exists on the server (the classic post-deploy white
 * screen). On a chunk-load failure this forces ONE clean reload — the fresh
 * HTML references the new chunk hashes and the app recovers invisibly.
 *
 * A sessionStorage flag guarantees we never reload-loop: if the chunk still
 * fails after a reload (real outage, adblock, offline), the error propagates
 * to RootErrorBoundary exactly as an ordinary render error would. The flag is
 * cleared on any successful chunk load, re-arming the guard for the next
 * deploy within the same tab session.
 */
import { lazy } from 'react';

const RELOAD_FLAG = 'cc_chunk_reloaded_v1';

export function lazyWithReload(factory) {
  return lazy(() =>
    factory()
      .then((module) => {
        try { sessionStorage.removeItem(RELOAD_FLAG); } catch (e) { /* storage unavailable — fine */ }
        return module;
      })
      .catch((error) => {
        let canReload = false;
        try {
          if (sessionStorage.getItem(RELOAD_FLAG) !== '1') {
            sessionStorage.setItem(RELOAD_FLAG, '1');
            canReload = true;
          }
        } catch (e) { /* storage unavailable — never risk a reload loop */ }
        if (canReload) {
          window.location.reload();
          // Keep React suspended while the page reloads (never resolves).
          return new Promise(() => {});
        }
        throw error;
      })
  );
}
